import type { ProjectDetail } from "@/mocks/admin/projects";

type Props = {
  pricing: ProjectDetail["pricing"];
  extraPaid?: number;
  onCollect?: () => void;
};

export default function PricingCard({ pricing, extraPaid = 0, onCollect }: Props) {
  const balanceDue = Math.max(0, pricing.balanceDue - extraPaid);
  const fullyPaid = balanceDue <= 0;

  const Row = ({ label, value, muted }: { label: string; value: string; muted?: boolean }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${muted ? "text-ink-500" : "text-ink-700"}`}>{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${muted ? "text-ink-500" : "text-ink-900"}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base font-bold text-ink-900">Pricing & Payment</h3>
        <button className="text-xs font-semibold text-brand-600 hover:text-brand-700 cursor-pointer">
          Edit quote
        </button>
      </div>

      <Row label="Subtotal (labor)" value={`$${pricing.subtotal.toLocaleString()}`} muted />
      <Row label="Materials" value={`$${pricing.materials.toLocaleString()}`} muted />
      <Row label="Fuel surcharge" value={`$${pricing.fuelSurcharge.toLocaleString()}`} muted />
      <Row label="Tax" value={`$${pricing.tax.toLocaleString()}`} muted />

      <div className="border-t border-ink-100 my-3"></div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-ink-900">Total</span>
        <span className="font-display text-2xl font-bold text-ink-900 tabular-nums">
          ${pricing.total.toLocaleString()}
        </span>
      </div>

      <div className="mt-4 rounded-md bg-cream-50 border border-ink-100 p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
            Deposit
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
              pricing.depositPaid
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                pricing.depositPaid ? "bg-emerald-500" : "bg-amber-500"
              }`}
            ></span>
            {pricing.depositPaid ? "Paid" : "Pending"}
          </span>
        </div>
        <div className="font-display text-lg font-bold text-ink-900 tabular-nums">
          ${pricing.deposit.toLocaleString()}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink-100">
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
            Balance {fullyPaid ? "collected" : "due on completion"}
          </span>
          {fullyPaid && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              <i className="ri-check-line"></i>
              Paid in full
            </span>
          )}
        </div>
        <div
          className={`font-display text-lg font-bold tabular-nums ${
            fullyPaid ? "text-emerald-700" : "text-brand-700"
          }`}
        >
          ${balanceDue.toLocaleString()}
        </div>
        {extraPaid > 0 && !fullyPaid && (
          <div className="text-[11px] text-ink-500 mt-1">
            ${extraPaid.toLocaleString()} collected · ${balanceDue.toLocaleString()} remaining
          </div>
        )}
        {extraPaid > 0 && fullyPaid && (
          <div className="text-[11px] text-emerald-700 mt-1 font-semibold">
            ${extraPaid.toLocaleString()} collected — balance cleared
          </div>
        )}
      </div>

      <button
        onClick={onCollect}
        disabled={fullyPaid}
        className={`w-full mt-4 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-md text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
          fullyPaid
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-not-allowed"
            : "bg-ink-900 hover:bg-ink-800 text-white"
        }`}
      >
        <i className={fullyPaid ? "ri-check-double-line" : "ri-bank-card-line"}></i>
        {fullyPaid ? "Balance fully collected" : "Collect Balance"}
      </button>
    </div>
  );
}